import { useToggleFavorite } from "@/api/resourceTags";
import type { ObjectActionHandlers } from "@/object-actions/types";
import { RESOURCE_TAG_FAVORITE, type Skill } from "@cohesive/api-client";
import { useMemo } from "react";

export interface SkillActionHandlerDeps {
  /** Open the skill editor (also the row's primary click). */
  edit: () => void;
  /** Flip auto/manual mode (enabled/disabled) — one callback for both toggles. */
  toggleEnabled: () => void;
  publish: () => void;
  moveToTrash: () => void;
}

/** Registry handlers for a Skills-rail skill row. */
export function useSkillObjectActionHandlers(
  skill: Skill,
  deps: SkillActionHandlerDeps,
): ObjectActionHandlers {
  const toggleFavorite = useToggleFavorite();
  const isFavorite = !!skill.tags?.includes(RESOURCE_TAG_FAVORITE);

  return useMemo((): ObjectActionHandlers => {
    const toggleFav = () =>
      toggleFavorite.mutate({
        resourceType: "skill",
        resourceId: skill.id,
        isFavorite,
      });
    return {
      star: () => window.setTimeout(toggleFav, 0),
      unstar: () => window.setTimeout(toggleFav, 0),
      edit: () => window.setTimeout(deps.edit, 0),
      "set-auto": () => window.setTimeout(deps.toggleEnabled, 0),
      "set-manual": () => window.setTimeout(deps.toggleEnabled, 0),
      publish: () => window.setTimeout(deps.publish, 0),
      delete: () => window.setTimeout(deps.moveToTrash, 0),
    };
  }, [deps, isFavorite, skill.id, toggleFavorite]);
}
